import _ from 'lodash';
import moment from "moment/moment";
import React,{ useState } from 'react';
import { CSVLink } from 'react-csv';
import { connect } from 'react-redux';
import Modal from 'react-responsive-modal';
import ReactTable from 'react-table';
import "react-table/react-table.css";

import IntrastatLineDetails from './InvoiceDetails';
import FileSaveAs from '../../downloaders/FileSaveAs';
import LanguageStringResolver from '../../fields/language/LanguageStringResolver';
import FormattedCurrencyValue from '../../fields/number/FormattedCurrencyValue';
import Pagination from '../../page/Pagination';
import FILING_CONTROLLER from "../../../controllers/filing";
import LANGUAGES_CONTROLLER from '../../../controllers/languages';
import { TableConfig } from '../../../utils/appConfig';
import { FeatureFlagEnum } from '../../../utils/enums';
import { withFullStoryExclude } from '../../../utils/fullStoryHelper';
import { sanitizeCsvData } from '../../../utils/csvSanitizer';
import Logger, { LOG_LEVELS } from '../../../utils/logger';

const log = new Logger('INTRA_FILING_VALUES', LOG_LEVELS.DEBUG);

const DEFAULT_PAGINATION_CONFIG = {
  pageNumber: 1,
  pageSize: TableConfig.PAGINATION.STANDARD_LEVEL_TWO_PAGE_SIZE,
  page: 0,
  startAt: 1
};

const LANGUAGE_PREFIX = 'Intra_';

const commercialSummaryColumns = [
  {
    Header: () => <LanguageStringResolver string="intrastat_summaryTransactionType" />,
    accessor: 'transactionType', id: 'transactionType'
  },
  {
    Header: () => <LanguageStringResolver string="intrastat_summaryTotalValue" />,
    accessor: 'totalValue', id: 'totalValue'
  },
  {
    Header: () => <LanguageStringResolver string="intrastat_summaryCurrency" />,
    accessor: 'totalValueCurrency', id: 'totalValueCurrency'
  }
];

const CommercialSummary = ({ featureFlag, goodsData, languages, loading, onClose, onOpen, open, summary }) => {
  const getCommercialSummaryTxt = () => {
    if (!summary) {
      throw new Error('Commercial summary is not available');
    }

    const text = [
      LANGUAGES_CONTROLLER.format(languages, 'intrastat_summaryNoOfDocuments'),
      '      : ',
      summary.numberOfDocuments,
      '\n',
      LANGUAGES_CONTROLLER.format(languages, 'intrastat_summaryNoOfCodes'),
      ': ',
      summary.numberOfIntrastatCodes,
      '\n\n',
      ' -------------------------------------------------- ',
      '\n\n',
      LANGUAGES_CONTROLLER.format(languages, 'intrastat_summaryTransactionType'),
      '\t',
      LANGUAGES_CONTROLLER.format(languages, 'intrastat_summaryTotalValue'),
    ];

    _.each(summary.totalSummary, t => {
      text.push(
        '\n',
        t.transactionType,
        '\t\t\t',
        t.totalValue,
        ' ',
        t.totalValueCurrency
      );
    });

    return { content: text.join(''), ext: 'txt' }
  };

  const showTotalsButton =
    featureFlag &&
    goodsData &&
    goodsData.length > 0;

  if (!showTotalsButton) {
    return null;
  }

  return <>
    <button className="button secondary" onClick={onOpen} data-testid="commercial summary">
      <LanguageStringResolver string="intrastat_summaryButton" />
    </button>
    <Modal
      classNames={{ modal: 'modal-full-width modal-max-width-75 modal-max-height-100' }}
      onClose={onClose}
      open={open}
    >
      <h2><LanguageStringResolver  string="intrastat_summaryTitle" /></h2>
      {
        loading ?
          <s-loader loading=" " data-testid="commercial summary loader"></s-loader> :
          <>
            <strong><LanguageStringResolver string="intrastat_summaryNoOfDocuments" />:</strong> {summary?.numberOfDocuments}
            <br />
            <strong><LanguageStringResolver string="intrastat_summaryNoOfCodes" />:</strong> {summary?.numberOfIntrastatCodes}
            <hr />
            <ReactTable
              columns={commercialSummaryColumns}
              data-testid="commercial summary table"
              data={summary?.totalSummary}
              maxRows={0}
              minRows={0}
              showPagination={false}
            />
            <FileSaveAs
              charset="utf-8"
              className="button primary"
              disabled={!summary}
              downloadLabelKey="intrastat_summarySaveButton"
              fetchContent={getCommercialSummaryTxt}
              filename={`intrastat_summary_${moment.utc().format('YYYY-MM-DD')}`}
              isBlob={false}
              mimeType="text/plain"
            />
            <button className="button secondary" onClick={onClose}>
              <LanguageStringResolver string="intrastat_summaryCloseButton" />
            </button>
          </>
      }
    </Modal>
  </>
};

const getColumns = columnData => columnData.map(
  item => ({
    id: item.Accessor,
    Header: () => <LanguageStringResolver string={LANGUAGE_PREFIX + item.Header} />,
    accessor:
      (item.DataType === 'Currency' &&
        //values in partner currency can be N/A, hence don't format those as currency values
        (item.Header !== 'CommercialValueInPartnerCurrency' &&
          item.Header !== 'StatisticalValueInPartnerCurrency' &&
          item.Header !== 'AmountInPartnerCurrency')) ?
        d => d[item.Accessor] != null && d[item.Accessor] !== '' ?
          <FormattedCurrencyValue value={d[item.Accessor]} inputFieldClassName={withFullStoryExclude()} /> :
          d[item.Accessor]
        : item.Accessor,
  })
);

const getDataForCSV = (data, columnsToAdd) => data.map(row => {
  const filteredRow = {};
  columnsToAdd.forEach(column => {
    const key = column.id;
    let value = row[key];

    if (value && typeof value === 'object' && value.props && 'value' in value.props) {
      value = value.props.value;
    }

    filteredRow[key] = _.defaultTo(value,'');
  });

  return filteredRow;
});

const getReactTableClassName = data => `-striped -highlight${_.isEmpty(data) ? '' : ' mb30'}`;
const getReactTableHeight = data => data.length > 8 ? '500px' : '100%';
const getCsvClassName = data => data.length ? 'button secondary' : 'invisible';

const IntrastatFilingValues = props => {
  const {
    companyId,
    countryCode,
    featureFlags,
    filingValues,
    intrastatCommercialSummary,
    languages,
    loadingIntrastatCommercialSummary,
    onOpenCommercialSummaryModal,
    onShowIntrastatDetails,
  } = props;

  const [isLineDetailsPopupOpen, setIsLineDetailsPopupOpen] = useState(false);
  const [isCommercialSummaryModalOpen, setIsCommercialSummaryModalOpen] = useState(false);
  const [currentLineDetails, setCurrentLineDetails] = useState({
    lineNumbers: [],
    totalCount: 0,
    commercialValue: 0,
    statisticalValue: 0
  });

  const onClickIntrastatGoodsLine = (e, state, rowInfo) => {
    const lineDetails = _.defaultTo(filingValues.goodsLines?.[rowInfo.index], {});

    onShowIntrastatDetails(lineDetails.lineNumbers, DEFAULT_PAGINATION_CONFIG, () => {
      setCurrentLineDetails({
        lineNumbers: lineDetails.lineNumbers,
        commercialValue: lineDetails.commercialValue,
        statisticalValue: lineDetails.statisticalValue,
        totalCount: lineDetails.lineNumbers.length
      });

      setIsLineDetailsPopupOpen(true);
    });
  };

  const onFetchData = (pagination) => {
    onShowIntrastatDetails(currentLineDetails.lineNumbers, pagination, () => { });
  };

  const onCloseModal = () => {
    setIsLineDetailsPopupOpen(false);
  };

  const doOpenCommercialSummaryModal = () => {
    setIsCommercialSummaryModalOpen(true);
    onOpenCommercialSummaryModal(
      companyId,
      countryCode,
      filingValues.startDate,
      filingValues.endDate,
      filingValues.reportType
    );
  };

  const doCloseCommercialSummaryModal = () => {
    setIsCommercialSummaryModalOpen(false);
  };

  log[LOG_LEVELS.DEBUG]('IntrastatFilingValues');
  log[LOG_LEVELS.DEBUG]('render(): ', props);
  const enableExportIntrastat = featureFlags?.[FeatureFlagEnum.ENABLE_EXPORT_INTRASTAT];

  //Get Goods lines
  const goodsData = _.defaultTo(filingValues.goodsLines,[]);
  const goodsColumnData = _.defaultTo(filingValues?.columnData?.goodsColumns, []);
  const goodsColumnsToAdd = getColumns(goodsColumnData);
  const fileName = `Intrastat_${new Date().toISOString().slice(0, 19).replace(/[-:]/g, "")}.csv`;

  //Get Service lines
  const serviceData = _.defaultTo(filingValues.serviceLines, []);
  const serviceColumnData = _.defaultTo(filingValues?.columnData?.serviceColumns, []);
  const serviceColumnsToAdd = getColumns(serviceColumnData);

  const modalClasses = {
    modal: 'modal-full-width-height modal-max-width-75'
  };

  return (
    <div>
      <br />
      <h2><LanguageStringResolver string='intrastat_goods' /></h2>
      <ReactTable
        PaginationComponent={Pagination}
        className={`${getReactTableClassName(goodsData)} horizontal-scroll-table`}
        columns={goodsColumnsToAdd}
        data={goodsData}
        defaultPageSize={TableConfig.PAGINATION.STANDARD_LEVEL_ONE_PAGE_SIZE}
        getTrProps={(state, rowInfo) => {
          return {
            onClick: e => {
              onClickIntrastatGoodsLine(e, state, rowInfo);
            }
          }
        }}
        maxRows={0}
        minRows={0}
        noDataText={LANGUAGES_CONTROLLER.format(languages,'ecListingFilingValues_NoData')}
        pageSizeOptions={TableConfig.PAGINATION.STANDARD_PAGE_SIZE_OPTIONS}
        resizable={true}
        showPagination={goodsData.length !== 0}
        style={{ height: getReactTableHeight(goodsData) }}
      />
      <div style={{ display: 'flex', gap: '10px' }}>
        {enableExportIntrastat &&
          <span data-csv-download="goods">
            <CSVLink
              data={sanitizeCsvData(getDataForCSV(goodsData, goodsColumnsToAdd))}
              headers={
                goodsColumnData.map(item => ({
                  key: item.Accessor,
                  label: LANGUAGES_CONTROLLER.format(languages,LANGUAGE_PREFIX + item.Header)
                }))
              }
              filename={fileName}
              className={getCsvClassName(goodsData)}
              role="button"
              disabled={goodsData?.length > 0 ? undefined : true}
            >
              <LanguageStringResolver string='intrastatView_ExportToCSV' />
            </CSVLink>
          </span>
        }
        <CommercialSummary
          featureFlag={featureFlags?.[FeatureFlagEnum.ENABLE_INTRASTAT_COMMERCIAL_SUMMARY]}
          goodsData={goodsData}
          languages={languages}
          loading={loadingIntrastatCommercialSummary}
          onClose={doCloseCommercialSummaryModal}
          onOpen={doOpenCommercialSummaryModal}
          open={isCommercialSummaryModalOpen}
          summary={intrastatCommercialSummary}
        />
      </div>
      <br />
      <div>
        <Modal open={isLineDetailsPopupOpen} onClose={onCloseModal} classNames={modalClasses}>
          <div style={{ padding: '10px' }}>
            <h2>{LANGUAGES_CONTROLLER.format(languages,'IntraLine_Header')}</h2>
            <div>
              <IntrastatLineDetails
                lineDetails={currentLineDetails}
                onFetchData={onFetchData}
              />
            </div>
          </div>
        </Modal>
      </div>
      {serviceData.length > 0 ? <div>
        <br />
        <h2><LanguageStringResolver string='intrastat_services' /></h2>
        <ReactTable
          PaginationComponent={Pagination}
          className={getReactTableClassName(serviceData)}
          columns={serviceColumnsToAdd}
          data={serviceData}
          defaultPageSize={TableConfig.PAGINATION.STANDARD_LEVEL_ONE_PAGE_SIZE}
          maxRows={0}
          minRows={0}
          noDataText={LANGUAGES_CONTROLLER.format(languages,'ecListingFilingValues_NoData')}
          pageSizeOptions={TableConfig.PAGINATION.STANDARD_PAGE_SIZE_OPTIONS}
          resizable={true}
          showPagination={serviceData.length !== 0}
          style={{ height: getReactTableHeight(serviceData) }}
        />
        {enableExportIntrastat &&
          <span data-csv-download="services">
            <CSVLink
              data={sanitizeCsvData(getDataForCSV(serviceData, serviceColumnsToAdd))}
              headers={
                serviceColumnData.map(item => ({
                  key: item.Accessor,
                  label: LANGUAGES_CONTROLLER.format(languages,LANGUAGE_PREFIX + item.Header)
                }))
              }
              filename={fileName}
              className={getCsvClassName(serviceData)}
            >
              <LanguageStringResolver string='intrastatView_ExportToCSV' />
            </CSVLink>
          </span>
        }
      </div> : null}
    </div>
  );
}

// Which props do we want to inject, given the global state?
function mapStateToProps(state) {
  return {
    featureFlags: state.featureFlag && state.featureFlag.data,
    intrastatCommercialSummary: state.filing.intrastatCommercialSummary,
    languages: state.languages,
    loadingIntrastatCommercialSummary: state.filing.loadingIntrastatCommercialSummary,
  };
}

function mapDispatchersToProps(dispatch) {
  return {
    onOpenCommercialSummaryModal: (companyId, countryCode, startDate, endDate, intrastatReportType) => {
      log[LOG_LEVELS.DEBUG]('onOpenCommercialSummaryModal');
      dispatch(FILING_CONTROLLER.doLoadIntrastatCommercialSummary(companyId, countryCode, startDate, endDate, intrastatReportType));
    },
    onShowIntrastatDetails: (lineNumbers, pagination, callback) => {
      log[LOG_LEVELS.DEBUG]('onShowIntrastatDetails: ', lineNumbers);
      dispatch(FILING_CONTROLLER.doLoadIntrastatDetails(lineNumbers, pagination, callback));
    },
  };
}



// Wrap the component to inject dispatch and state into it
export default connect(mapStateToProps, mapDispatchersToProps)(IntrastatFilingValues);
